import type {
  GoogleAutoChartFamily,
  PortalGoogleAutoPanel,
  PortalGoogleAutomotiveReport,
} from '../../../../../types/portalAutomotive';
import type { PortalGoogleReport, PortalReportLabels } from '../../../../../types/portalReport';
import {
  GOOGLE_AUTO_NONE_SENTENCE,
  googleAutoNothingRan,
  googleAutoPanelSpecFor,
  googleAutoPanelVisible,
  googleCostPerOrNull,
  googleCtrOrNull,
} from '../../../../../lib/portalReport/automotiveGoogle';
import type { ChartMetric } from '../../../../../lib/portalReport/chartPlan';
import { Band, Figure, SectionHeading } from './controls';
import { ReportChartCard } from './ReportChartCard';
import { ReportHero, type HeroStat } from './ReportHero';
import { CompetitivePanel } from './ReportPanels';
import { SearchTermsTable } from './ReportTables';
import { DASH, formatReportDate, moneyFull, moneyRound, numberFull, oneDecimal, percent } from './format';

// CLIENT PORTAL RESTYLE — the AUTOMOTIVE Google page (2026-11-12, Round 5 §C).
//
// ⚠️⚠️ WHICH CAMPAIGN IS IN WHICH GROUP is `campaignGroup.ts` (server) and nowhere
// else; WHETHER a group is drawn is `googleAutoPanelVisible`. This file only draws
// them. Every group renders only when it has something in the selected OR the
// comparison period, and each is a FULL-WIDTH section (never two columns), the same
// shape as the Meta page.
//
// ⚠️ CONVERSIONS ARE FRACTIONAL (data-driven attribution) and print to ONE decimal
// everywhere, including the chart tooltip (`fractionalCounts`).
//
// ⚠️ Google's "no data" gap wording carries NO reason: our history for an account
// can start long after it began spending, which is not the same as "no spend".

/** The metric chips every Google card offers: conversions, cost per conversion, spend, clicks. */
function googleChips(family: GoogleAutoChartFamily): ChartMetric[] {
  return family.currency !== null ? ['leads', 'costPerLead', 'spend', 'clicks'] : ['leads', 'clicks'];
}

/**
 * One Google chart family as a titled chart card ("<section> · <metric>"), with the
 * conversion vocabulary on the count and the cost-per. ⚠️ A blank section throws
 * inside the card — a card cannot render without a title.
 */
export function GoogleAutoFamilyChart({
  family,
  section,
  labels,
  fallbackCurrency,
  today,
}: {
  family: GoogleAutoChartFamily;
  section: string;
  labels: PortalReportLabels;
  fallbackCurrency: string;
  today: string;
}) {
  return (
    <ReportChartCard
      title={section}
      titleSection={section}
      today={today}
      fractionalCounts
      source={{ kind: 'goal', period: family.period, months: family.months }}
      metrics={googleChips(family)}
      labels={{
        ...labels,
        leads: 'Conversions',
        leadsShort: 'Conversions',
        costPerLead: 'Cost per conversion',
      }}
      currency={family.currency ?? fallbackCurrency}
      periodLabel="this period"
    />
  );
}

function spendText(spend: Array<{ currency: string; spend: number }>): string | null {
  return spend.length > 0 ? spend.map((m) => moneyFull(m.spend, m.currency)).join(' · ') : null;
}

export function AutomotiveGoogleHeadline({
  google,
  range,
}: {
  google: PortalGoogleAutomotiveReport;
  range: { from: string; to: string };
}) {
  const h = google.headline;
  const stat = (label: string, value: string | null): HeroStat => ({
    label,
    value,
    delta: null,
    metric: 'leads',
    unavailable: null,
  });
  return (
    <ReportHero
      eyebrow={`Google · ${formatReportDate(range.from)} – ${formatReportDate(range.to)}`}
      headline={oneDecimal(h.conversions)}
      intro="Google conversions for this period. Conversions are fractional because Google shares credit between the ads a person saw."
      stats={[
        stat('Total spend', spendText(h.spend)),
        stat('Clicks', numberFull(h.clicks)),
        stat('Impressions', numberFull(h.impressions)),
      ]}
    />
  );
}

function costText(money: PortalGoogleAutoPanel['money'], pick: 'costPerConversion' | 'costPerClick'): string | null {
  if (money.length === 0) return null;
  return money
    .map((m) => {
      const v = m[pick];
      return v === null ? DASH : moneyFull(v, m.currency);
    })
    .join(' · ');
}

function GoogleCampaignTable({ panel }: { panel: PortalGoogleAutoPanel }) {
  if (panel.campaigns.length === 0) return null;
  return (
    <div className="rpt-table-wrap" style={{ marginTop: 14 }}>
      <table className="rpt-table">
        <caption className="rpt-visually-hidden">{googleAutoPanelSpecFor(panel.group).title}: campaigns</caption>
        <thead>
          <tr>
            <th scope="col">Campaign</th>
            <th scope="col">Spend</th>
            <th scope="col">Impressions</th>
            <th scope="col">Clicks</th>
            <th scope="col">Conversions</th>
            <th scope="col">Cost per conversion</th>
          </tr>
        </thead>
        <tbody>
          {panel.campaigns.map((c) => {
            const cost = googleCostPerOrNull(c.spend, c.conversions);
            return (
              <tr key={`${c.campaignId}-${c.currency}`}>
                <th scope="row">{c.name}</th>
                <td>{moneyRound(c.spend, c.currency)}</td>
                <td>{numberFull(c.impressions)}</td>
                <td>{numberFull(c.clicks)}</td>
                <td>{oneDecimal(c.conversions)}</td>
                <td>{cost === null ? DASH : moneyFull(cost, c.currency)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function GoogleGroupSection({
  panel,
  labels,
  today,
  alternate,
}: {
  panel: PortalGoogleAutoPanel;
  labels: PortalReportLabels;
  today: string;
  alternate: boolean;
}) {
  const spec = googleAutoPanelSpecFor(panel.group);
  const ctr = googleCtrOrNull(panel.counts);
  const fallbackCurrency = panel.money[0]?.currency ?? 'AUD';
  return (
    <Band variant={alternate ? 'contrast' : undefined}>
      <section aria-label={spec.title} data-google-group={panel.group} data-auto-section>
        <SectionHeading title={spec.title} />
        <div className="rpt-card">
          <div className="rpt-stat-grid">
            <p className="rpt-stat">
              <Figure value={spendText(panel.money.map((m) => ({ currency: m.currency, spend: m.spend })))} className="rpt-card-figure" />
              <span className="rpt-mono rpt-stat-label">Spend</span>
            </p>
            <p className="rpt-stat">
              <Figure value={numberFull(panel.counts.impressions)} className="rpt-card-figure" />
              <span className="rpt-mono rpt-stat-label">Impressions</span>
            </p>
            <p className="rpt-stat">
              <Figure value={numberFull(panel.counts.clicks)} className="rpt-card-figure" />
              <span className="rpt-mono rpt-stat-label">Clicks</span>
            </p>
            <p className="rpt-stat">
              <Figure value={ctr === null ? null : percent(ctr)} className="rpt-card-figure" />
              <span className="rpt-mono rpt-stat-label">Click-through rate</span>
            </p>
            <p className="rpt-stat">
              <Figure value={costText(panel.money, 'costPerClick')} className="rpt-card-figure" />
              <span className="rpt-mono rpt-stat-label">Cost per click</span>
            </p>
            <p className="rpt-stat">
              <Figure value={oneDecimal(panel.counts.conversions)} className="rpt-card-figure" />
              <span className="rpt-mono rpt-stat-label">Conversions</span>
            </p>
            <p className="rpt-stat">
              <Figure value={costText(panel.money, 'costPerConversion')} className="rpt-card-figure" />
              <span className="rpt-mono rpt-stat-label">Cost per conversion</span>
            </p>
          </div>
        </div>
        <div className="rpt-card-grid" style={{ marginTop: 14 }}>
          <GoogleAutoFamilyChart
            family={panel.chart}
            section={spec.title}
            labels={labels}
            fallbackCurrency={fallbackCurrency}
            today={today}
          />
        </div>
        <GoogleCampaignTable panel={panel} />
      </section>
    </Band>
  );
}

/**
 * The groups, in spec order, hide-when-empty applied, each FULL WIDTH. One sentence
 * when no group ran at all, never four empty sections.
 */
export function AutomotiveGoogleGroups({
  google,
  labels,
  today,
}: {
  google: PortalGoogleAutomotiveReport;
  labels: PortalReportLabels;
  today: string;
}) {
  if (googleAutoNothingRan(google)) {
    return (
      <Band>
        <SectionHeading title="Results by campaign type" />
        <p className="rpt-note rpt-muted" data-google-none>
          {GOOGLE_AUTO_NONE_SENTENCE}
        </p>
      </Band>
    );
  }
  const visible = google.panels.filter(googleAutoPanelVisible);
  return (
    <>
      <Band>
        <SectionHeading
          title="Results by campaign type"
          intro="Each campaign is counted in one section. Vehicle listing ads (VLA) are Performance Max campaigns with VLA in their name; Search and the other Performance Max campaigns are shown separately."
        />
      </Band>
      {visible.map((p, i) => (
        <GoogleGroupSection key={p.group} panel={p} labels={labels} today={today} alternate={i % 2 === 1} />
      ))}
    </>
  );
}

/**
 * The whole automotive Google page: headline, the all-campaigns chart, the groups,
 * then the standard competitive-position and search-terms sections the page always
 * carried (each omitted on its own when empty).
 */
export function AutomotiveGooglePage({
  google,
  range,
  labels,
  today,
  report,
}: {
  google: PortalGoogleAutomotiveReport;
  range: { from: string; to: string };
  labels: PortalReportLabels;
  today: string;
  /** The page's own payload — only for the sections that are not grouped. Optional for tests. */
  report?: PortalGoogleReport;
}) {
  const fallbackCurrency = google.chart.currency ?? 'AUD';
  return (
    <>
      <AutomotiveGoogleHeadline google={google} range={range} />
      <Band>
        <SectionHeading
          title="Google advertising results"
          intro="Choose between the past 12 months and the reporting period."
        />
        <div className="rpt-card-grid">
          <GoogleAutoFamilyChart
            family={google.chart}
            section="All Google campaigns"
            labels={labels}
            fallbackCurrency={fallbackCurrency}
            today={today}
          />
        </div>
      </Band>
      <AutomotiveGoogleGroups google={google} labels={labels} today={today} />
      {report && (
        <Band variant="contrast">
          <CompetitivePanel competitive={report.competitive} />
        </Band>
      )}
      {report && report.searchTerms.length > 0 && (
        <Band>
          <SectionHeading
            title="What people searched for"
            intro="The actual words typed before a click. Sort any column."
          />
          <SearchTermsTable
            rows={report.searchTerms}
            labels={labels}
            capped={report.searchTermsCapped}
            total={report.searchTermsTotal}
          />
        </Band>
      )}
    </>
  );
}
